import isEqual from "lodash/isEqual";
import pick from "lodash/pick";

export type ViewportType = {
  latitude: number;
  longitude: number;
  zoom: number;
  maxZoom: number;
  minZoom: number;
  pitch: number;
  bearing: number;
};

export const DEFAULT_VIEWPORT: ViewportType = {
  minZoom: 1,
  maxZoom: 20,
  zoom: 2,
  latitude: 0,
  longitude: 0,
  pitch: 0,
  bearing: 0,
};

// https://docs.mapbox.com/mapbox-gl-js/guides/projections/
export enum MapProjection {
  MERCATOR = "mercator",
  GLOBE = "globe",
}

export const MAP_PROJECTIONS: MapProjection[] = Object.values(MapProjection);

export const DEFAULT_PROJECTION: MapProjection = MapProjection.MERCATOR;

export const isValidProjection = (value: string): value is MapProjection =>
  (MAP_PROJECTIONS as string[]).includes(value);

export const RELEVANT_VIEWPORT_ATTRIBUTES = [
  "latitude",
  "longitude",
  "minZoom",
  "maxZoom",
  "zoom",
  "pitch",
  "bearing",
];

export const isValidViewport = ({
  latitude,
  longitude,
  zoom,
  maxZoom,
  minZoom,
  pitch,
  bearing,
}: ViewportType) => {
  return (
    [latitude, longitude, zoom, maxZoom, minZoom, pitch, bearing].every((n) =>
      Number.isFinite(n)
    ) &&
    zoom <= maxZoom &&
    zoom >= minZoom &&
    isValidUrlCoordinateGroup({ latitude, longitude, zoom })
  );
};

export const clampViewportZoom = (viewport: ViewportType) => {
  const { zoom, minZoom, maxZoom } = viewport;

  if (zoom < minZoom) {
    return {
      ...viewport,
      zoom: minZoom,
    };
  } else if (zoom > maxZoom) {
    return {
      ...viewport,
      zoom: minZoom,
    };
  }

  return viewport;
};

export const mergeViewports = (
  newViewportModel: Partial<ViewportType>,
  oldViewport: ViewportType
) => {
  const newViewport = clampViewportZoom(
    pick(
      {
        ...DEFAULT_VIEWPORT,
        ...newViewportModel,
      },
      RELEVANT_VIEWPORT_ATTRIBUTES
    ) as ViewportType
  );

  const hasChanged = !isEqual(
    newViewport,
    pick(oldViewport, RELEVANT_VIEWPORT_ATTRIBUTES)
  );

  return hasChanged ? newViewport : oldViewport;
};

export const viewportAttributeValues = (viewport: ViewportType) =>
  RELEVANT_VIEWPORT_ATTRIBUTES.map((k) => viewport[k] ?? null);

export interface IUrlCoordinates {
  latitude: number;
  longitude: number;
  zoom: number;
}

export const toUrlCoordinateNumber = (value: string): number =>
  Number(parseFloat(value).toFixed(7));

export const isValidCoordinate = (value: number, limit: number): boolean =>
  Number.isFinite(value) && value >= -1 * limit && value <= limit;

export const isValidLatitude = (value: number): boolean =>
  isValidCoordinate(value, 90);

export const isValidLongitude = (value: number): boolean =>
  isValidCoordinate(value, 180);

// Longitudes off a repeated world copy come back unwrapped (a click at 190, a
// camera fitted across the antimeridian); normalize into [-180, 180], keeping
// the hemisphere at exactly +/-180.
export const wrapLongitude = (lng: number): number => {
  const wrapped = ((((lng + 180) % 360) + 360) % 360) - 180;
  return wrapped === -180 && lng >= 180 ? 180 : wrapped;
};

// The box to fit a camera to: `fitBbox2d` where a place has one, otherwise the
// plain `bbox2d`, and nothing at all when it has neither.
export const cameraBbox = (place?: {
  fitBbox2d?: number[];
  bbox2d?: number[];
}): number[] | undefined => {
  if (place?.fitBbox2d?.length) {
    return place.fitBbox2d;
  }
  return place?.bbox2d?.length ? place.bbox2d : undefined;
};

// https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/?size=n_10_n#maxzoom
export const isValidZoom = (value: number): boolean =>
  Number.isFinite(value) && value >= 0 && value <= 24;

export const isValidUrlCoordinateGroup = (value: IUrlCoordinates) => {
  return (
    isValidLatitude(value.latitude) &&
    isValidLongitude(value.longitude) &&
    isValidZoom(value.zoom)
  );
};

export const getMapViewport = (mapLib) => {
  const { lng: longitude, lat: latitude } = mapLib.getCenter();
  const zoom = mapLib.getZoom();
  const minZoom = mapLib.getMinZoom();
  const maxZoom = mapLib.getMaxZoom();
  const pitch = mapLib.getPitch();
  const bearing = mapLib.getBearing();

  return { longitude, latitude, zoom, minZoom, maxZoom, pitch, bearing };
};
